import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { PageHeader, Loading, ErrorState, Status } from "../components/Layout";
import { useApi, useResource, usePoll, uid } from "../lib/api";
import { useMe } from "../lib/me";
import RightsPanel from "../components/studio/RightsPanel";
import SourcesPanel from "../components/studio/SourcesPanel";
import ArrangePanel from "../components/studio/ArrangePanel";
import Workspace from "../components/studio/Workspace";
import VersionsPanel from "../components/studio/VersionsPanel";

const STEPS = [["rights", "1 · Rights"], ["sources", "2 · Sources & analysis"], ["arrange", "3 · Arrange"], ["workspace", "4 · Producer workspace"], ["versions", "5 · Versions & export"]];

export default function Project() {
  const { pid } = useParams();
  const api = useApi();
  const { reload: reloadMe } = useMe();
  const project = useResource(`/projects/${pid}`);
  const assets = useResource(`/projects/${pid}/assets`);
  const arrangements = useResource(`/projects/${pid}/arrangements`);
  const versions = useResource(`/projects/${pid}/versions`);
  const meta = useResource("/meta");
  const [jobs, setJobs] = useState([]);
  const [tab, setTab] = useState(null);
  const prevActive = useRef(0);

  const loadJobs = useCallback(async () => {
    try {
      const js = await api("GET", `/jobs?project_id=${pid}`);
      const active = js.filter((j) => ["queued", "running", "cancelling"].includes(j.status)).length;
      if (active < prevActive.current) { project.reload(); assets.reload(); arrangements.reload(); reloadMe(); }
      prevActive.current = active;
      setJobs(js);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, pid]);
  useEffect(() => { loadJobs(); }, [loadJobs]);
  const anyActive = jobs.some((j) => ["queued", "running", "cancelling"].includes(j.status));
  usePoll(loadJobs, anyActive, 1500);

  const runJob = async (type, body = {}) => {
    try {
      const j = await api("POST", `/projects/${pid}/jobs`, { type, idempotency_key: uid(), params: {}, ...body });
      toast.success(`${type.replace("_", " ")} queued (${j.credits} credit${j.credits === 1 ? "" : "s"} reserved)`);
      prevActive.current += 1; setJobs((js) => [j, ...js]); reloadMe(); return j;
    } catch (e) { toast.error(e.message); return null; }
  };

  useEffect(() => {
    if (tab || !project.data) return;
    const p = project.data;
    setTab(!p.rights ? "rights" : !p.analysis ? "sources" : !p.draft?.arrangement_id ? "arrange" : "workspace");
  }, [project.data, tab]);

  if (project.loading && !project.data) return <Loading />;
  if (project.error) return <ErrorState error={project.error} onRetry={project.reload} />;
  const p = project.data;
  const ctx = { p, pid, api, assets, arrangements, versions, meta: meta.data, jobs, runJob, reloadProject: project.reload, setTab };
  return (
    <div>
      <Link to="/studio" className="muted text-sm inline-flex items-center gap-1 mb-3 hover:text-white"><ArrowLeft size={14} />All projects</Link>
      <PageHeader title={p.title} sub={`${p.source_type === "isolated_vocal" ? "Isolated vocal" : "Stereo master"} · private project`} actions={<Status value={p.status} testid="project-status" />} />
      {p.quarantined && <div className="card p-4 mb-4 border-[#ff2d7555] text-sm" data-testid="quarantine-banner">This project is quarantined after an integrity failure ({p.quarantine_reason}). Exports are blocked and operations have been alerted.</div>}
      <div className="flex gap-2 scroll-x pb-2 mb-6" role="tablist">
        {STEPS.map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`step-${k}`}
          className={`btn btn-sm whitespace-nowrap ${tab === k ? "btn-primary" : "btn-ghost"}`}>{l}</button>)}
      </div>
      {jobs.length > 0 && <JobStrip jobs={jobs.slice(0, 4)} api={api} reload={loadJobs} />}
      {tab === "rights" && <RightsPanel {...ctx} />}
      {tab === "sources" && <SourcesPanel {...ctx} />}
      {tab === "arrange" && <ArrangePanel {...ctx} />}
      {tab === "workspace" && <Workspace {...ctx} />}
      {tab === "versions" && <VersionsPanel {...ctx} />}
    </div>
  );
}

function JobStrip({ jobs, api, reload }) {
  const cancel = async (j) => { try { await api("POST", `/jobs/${j.id}/cancel`); reload(); } catch (e) { toast.error(e.message); } };
  return (
    <div className="card p-3 mb-6 space-y-2" data-testid="job-strip">
      {jobs.map((j) => (
        <div key={j.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`job-${j.id}`}>
          <Status value={j.status} /><span className="font-medium">{j.type.replace("_", " ")}</span><span className="muted">stage: {j.stage}</span>
          {j.elapsed_s && <span className="muted">{j.elapsed_s}s</span>}
          {j.error && <span className="text-[#ff6b8f]">{j.error}</span>}
          {["queued", "running"].includes(j.status) && <button className="btn btn-ghost btn-sm ml-auto" onClick={() => cancel(j)} data-testid={`cancel-job-${j.id}`}>Cancel</button>}
        </div>))}
    </div>
  );
}
